import type { JSX } from "preact";
import { useEffect } from "preact/hooks";
import {
  clearSearch,
  closeComposerSafe,
  closeEmlPreview,
  moveMessage,
  openCompose,
  openComposeFor,
  openMessage,
  toggleFlagged,
  toggleSeen,
} from "../actions";
import {
  composer,
  emlPreview,
  emlPreviewError,
  emlPreviewLoading,
  fullMessage,
  isSearching,
  mailboxes,
  searchResults,
  selectedUid,
  showShortcuts,
  visibleMessages,
} from "../state";

function isEditing(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  if (el.getAttribute("contenteditable") === "true") return true;
  return false;
}

/**
 * Escape-key priority chain: closes the topmost overlay first. Exported
 * (rather than inlined in the switch) so the priority order is unit-testable
 * without a DOM/event-dispatch harness — signals in, action out.
 */
export function handleEscape(): void {
  if (emlPreview.value !== null || emlPreviewLoading.value || emlPreviewError.value !== null) {
    closeEmlPreview();
  } else if (composer.value !== null) {
    closeComposerSafe();
  } else if (isSearching.value) {
    clearSearch();
  } else if (showShortcuts.value) {
    showShortcuts.value = false;
  } else {
    selectedUid.value = null;
    fullMessage.value = null;
  }
}

export function KeyboardHandler(): null {
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent): void {
      if (isEditing()) return;

      const key = e.key;

      switch (key) {
        case "j":
        case "k": {
          const list = isSearching.value
            ? (searchResults.value ?? [])
            : visibleMessages.value;
          if (list.length === 0) return;
          const currentIndex = list.findIndex((m) => m.uid === selectedUid.value);
          let nextIndex: number;
          if (key === "j") {
            nextIndex = currentIndex === -1 ? 0 : Math.min(currentIndex + 1, list.length - 1);
          } else {
            nextIndex = currentIndex === -1 ? 0 : Math.max(currentIndex - 1, 0);
          }
          const nextMsg = list[nextIndex];
          if (nextMsg) {
            void openMessage(nextMsg.uid);
            requestAnimationFrame(() => {
              document
                .querySelector(`[data-uid="${String(nextMsg.uid)}"]`)
                ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
            });
          }
          break;
        }

        case "r": {
          if (fullMessage.value) {
            openComposeFor("reply");
          }
          break;
        }

        case "f": {
          if (fullMessage.value) {
            openComposeFor("forward");
          }
          break;
        }

        case "e": {
          const uid = selectedUid.value;
          if (uid != null) {
            const archiveMbox = mailboxes.value.find((mb) => mb.specialUse === "archive");
            const archivePath = archiveMbox?.path ?? "Archive";
            void moveMessage(uid, archivePath);
          }
          break;
        }

        case "#": {
          const uid = selectedUid.value;
          if (uid != null) {
            const trashMbox = mailboxes.value.find((mb) => mb.specialUse === "trash");
            const trashPath = trashMbox?.path ?? "Trash";
            void moveMessage(uid, trashPath);
          }
          break;
        }

        case "u": {
          const uid = selectedUid.value;
          if (uid != null) {
            void toggleSeen(uid);
          }
          break;
        }

        case "s": {
          const uid = selectedUid.value;
          if (uid != null) {
            void toggleFlagged(uid);
          }
          break;
        }

        case "c": {
          openCompose();
          break;
        }

        case "/": {
          e.preventDefault();
          const input = document.querySelector<HTMLInputElement>('input[placeholder="Search…"]');
          input?.focus();
          break;
        }

        case "Escape": {
          handleEscape();
          break;
        }

        case "?": {
          showShortcuts.value = !showShortcuts.value;
          break;
        }

        default:
          break;
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return null;
}

const SHORTCUTS: Array<{ key: string; description: string }> = [
  { key: "j", description: "Next message" },
  { key: "k", description: "Previous message" },
  { key: "r", description: "Reply" },
  { key: "f", description: "Forward" },
  { key: "e", description: "Archive" },
  { key: "#", description: "Move to Trash" },
  { key: "u", description: "Toggle read/unread" },
  { key: "s", description: "Toggle star" },
  { key: "c", description: "Compose new message" },
  { key: "/", description: "Focus search" },
  { key: "Esc", description: "Close / deselect" },
  { key: "?", description: "Toggle this help" },
];

export function ShortcutsModal(): JSX.Element | null {
  if (!showShortcuts.value) return null;

  return (
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-scrim"
      onClick={(e) => {
        if (e.target === e.currentTarget) showShortcuts.value = false;
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
    >
      <div class="glass max-w-md w-full rounded-xl p-6 shadow-2xl ring-1 ring-strong">
        <div class="mb-4 flex items-center justify-between">
          <h2 class="text-base font-semibold text-fg">Keyboard Shortcuts</h2>
          <button
            class="icon-btn"
            onClick={() => { showShortcuts.value = false; }}
            aria-label="Close shortcuts"
          >
            ✕
          </button>
        </div>
        <div class="grid grid-cols-2 gap-x-6 gap-y-2">
          {SHORTCUTS.map(({ key, description }) => (
            <div key={key} class="flex items-center gap-3">
              <kbd class="min-w-[2rem] rounded bg-ink-3 px-2 py-0.5 text-center text-xs font-mono text-fg-dim">
                {key}
              </kbd>
              <span class="text-sm text-fg-dim">{description}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
